import React, { useState, useCallback, useEffect } from 'react';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { TimesheetEntry } from '../types/timesheet.ts';
import { Employee } from '../types/employee.ts';
import { Upload, Download, FileText, Calculator, ArrowRight, CheckCircle } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { invoiceNumberingService } from '../services/invoiceNumberingService.ts';

interface ProcessedTimesheetData {
  'INV #': string;
  'EMPLOYEE': string;
  'PAY TYPE': string;
  'Activity Code': string;
  'Activity Description': string;
  'JOB NAME': string;
  'JOB NUMBER': string;
  'WEEK ENDING': string;
  'HOURS': number;
  'BURDENED RATE': number;
  'SOURCE AMOUNT': number;
}

const TimesheetProcessor: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [processedData, setProcessedData] = useState<ProcessedTimesheetData[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [excelHeaders, setExcelHeaders] = useState<string[]>([]);
  const [showMapping, setShowMapping] = useState(false);
  const [weekEndingDate, setWeekEndingDate] = useState('');
  const [manualWeekEndingDate, setManualWeekEndingDate] = useState('');
  
  // Contractor selection
  const [contractors, setContractors] = useState<any[]>([]);
  const [selectedContractor, setSelectedContractor] = useState<{id: string, name: string, markupPercentage: number} | null>(null);
  const [columnMapping, setColumnMapping] = useState({
    firstName: '',
    lastName: '',
    jobName: '',
    jobNumber: '',
    activityCode: '',
    activityDescription: '',
    payType: '',
    hours: '',
    burdened: '',
    total: ''
  });
  const [columnExamples, setColumnExamples] = useState<any>({});
  const [autoSplitJobName, setAutoSplitJobName] = useState(false);
  const [skippedPayTypes, setSkippedPayTypes] = useState<{ value: string; count: number }[]>([]);

  // Load saved mapping from localStorage
  useEffect(() => {
    const savedMapping = localStorage.getItem('timesheetColumnMapping');
    if (savedMapping) {
      try {
        setColumnMapping(JSON.parse(savedMapping));
      } catch (error) {
        console.error('Error loading saved column mapping:', error);
      }
    }
    
    // Set default week ending to last Friday
    const lastFriday = getLastFriday();
    setManualWeekEndingDate(lastFriday.toISOString().split('T')[0]);
    
    // Load contractors
    loadContractors();
  }, []);

  const loadContractors = async () => {
    try {
      const contractorsRef = collection(db, 'contractors');
      const q = query(contractorsRef, where('status', '==', 'active'));
      const snapshot = await getDocs(q);
      
      const contractorData = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.companyName || data.name || 'Unknown',
          markupPercentage: data.markupPercentage || 4.00
        };
      });
      
      // Add default contractors if none exist
      if (contractorData.length === 0) {
        contractorData.push(
          { id: 'cec', name: 'CEC', markupPercentage: 4.00 },
          { id: 'reliable', name: 'Reliable', markupPercentage: 4.50 },
          { id: 'btc', name: 'BTC', markupPercentage: 25.00 }
        );
      }
      
      setContractors(contractorData);
      
      // Auto-select the first contractor (CEC as default)
      if (contractorData.length > 0) {
        setSelectedContractor(contractorData[0]);
      }
    } catch (err) {
      console.error('Error loading contractors:', err);
      // Fallback to default contractors
      const defaultContractors = [
        { id: 'cec', name: 'CEC', markupPercentage: 4.00 },
        { id: 'reliable', name: 'Reliable', markupPercentage: 4.50 },
        { id: 'btc', name: 'BTC', markupPercentage: 25.00 }
      ];
      setContractors(defaultContractors);
      setSelectedContractor(defaultContractors[0]);
    }
  };

  const getLastFriday = () => {
    const today = new Date();
    // Create date at noon to avoid timezone offset issues
    const currentDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 12, 0, 0);
    const dayOfWeek = currentDate.getDay();
    const daysToSubtract = dayOfWeek === 0 ? 2 : (dayOfWeek + 2) % 7; // Get last Friday
    const lastFriday = new Date(currentDate);
    lastFriday.setDate(currentDate.getDate() - daysToSubtract);
    return lastFriday;
  };

  // Clean currency strings to numeric values
  const cleanCurrency = (value: any) => {
    if (typeof value === 'string') {
      // Remove $ and commas, then parse as float
      const cleaned = value.replace(/[$,]/g, '').trim();
      return cleaned === '' ? 0 : parseFloat(cleaned);
    }
    return typeof value === 'number' ? value : 0;
  };

  // Split combined job name/number field
  const splitJobNameNumber = (combinedValue: string): { jobNumber: string; jobName: string } => {
    if (!combinedValue || typeof combinedValue !== 'string') {
      return { jobNumber: '', jobName: '' };
    }

    console.log('Splitting:', combinedValue); // Debug log

    // Pattern 1: ##-##### format (like 23-25020)
    const pattern1 = /^(\d{2}-\d{5})\s+(.+)$/;
    const match1 = combinedValue.match(pattern1);
    if (match1) {
      console.log('Pattern 1 matched:', match1[1], '|', match1[2]); // Debug log
      return {
        jobNumber: match1[1].trim(),
        jobName: match1[2].trim()
      };
    }

    // Pattern 2: CI##-## format (like CI23-52)  
    const pattern2 = /^(CI\d{2}-\d{2})\s+(.+)$/;
    const match2 = combinedValue.match(pattern2);
    if (match2) {
      console.log('Pattern 2 matched:', match2[1], '|', match2[2]); // Debug log
      return {
        jobNumber: match2[1].trim(),
        jobName: match2[2].trim()
      };
    }

    // Pattern 3: More flexible - any job code followed by space and description
    const pattern3 = /^([A-Z0-9-]{4,12})\s+(.+)$/;
    const match3 = combinedValue.match(pattern3);
    if (match3) {
      console.log('Pattern 3 matched:', match3[1], '|', match3[2]); // Debug log
      return {
        jobNumber: match3[1].trim(),
        jobName: match3[2].trim()
      };
    }

    console.log('No pattern matched, keeping original:', combinedValue); // Debug log
    // If no pattern matches, return original as job name with empty job number
    return {
      jobNumber: '',
      jobName: combinedValue.trim()
    };
  };

  // Extract headers from Excel file
  const extractHeaders = useCallback((file: File) => {
    setIsLoading(true);
    setError(null);
    setExcelFile(file);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // First, try to detect if the file has headers
        // Get the range of the sheet
        const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
        const hasHeaders = range.e.r > 0; // More than one row
        
        // Get column headers - either from the first row or generate them
        let headers: string[] = [];
        let examples: any = {};
        
        if (hasHeaders) {
          // Try to read with headers first
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { raw: false });
          
          if (jsonData.length > 0) {
            headers = Object.keys(jsonData[0] as Record<string, any>);
            
            // Extract examples from the first row for each column
            headers.forEach(header => {
              examples[header] = (jsonData[0] as any)[header];
            });
          }
        }
        
        // If no headers were found or the file has only one row, generate column letters
        if (headers.length === 0) {
          // Read without headers
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
            raw: false,
            header: 'A' // Use A, B, C as headers
          });
          
          if (jsonData.length > 0) {
            headers = Object.keys(jsonData[0] as Record<string, any>);
            
            // Extract examples from the first row for each column
            headers.forEach(header => {
              examples[header] = (jsonData[0] as any)[header];
            });
          } else {
            setError('No data found in Excel file');
            setIsLoading(false);
            return;
          }
        }
        
        setExcelHeaders(headers);
        setColumnExamples(examples);
        
        // Auto-apply smart column mapping when file is loaded
        setTimeout(() => {
          const newMapping = autoMapColumns(headers);
          const mappedCount = Object.values(newMapping).filter(Boolean).length;
          if (mappedCount > 0) {
            console.log(`Auto-mapped ${mappedCount} columns successfully`);
          }
        }, 100);
        
        setShowMapping(true);
        setIsLoading(false);
      } catch (err) {
        console.error('Error reading file:', err);
        setError(`Error reading file: ${err}`);
        setIsLoading(false);
      }
    };
    
    reader.onerror = () => {
      setError('Error reading file');
      setIsLoading(false);
    };
    
    reader.readAsArrayBuffer(file);
  }, []);

  // Save mapping to localStorage
  const saveMapping = () => {
    localStorage.setItem('timesheetColumnMapping', JSON.stringify(columnMapping));
  };

  // Smart column mapping function
  const autoMapColumns = (headersToUse: string[] = excelHeaders) => {
    // Enhanced smart column detection with fuzzy matching and common variations
    const smartColumnMappings = {
      firstName: {
        exact: ['first name', 'firstname', 'first', 'fname', 'f_name', 'given name'],
        contains: ['first', 'given', 'fname'],
        patterns: [/^f\.?\s*name$/i, /^first/i]
      },
      lastName: {
        exact: ['last name', 'lastname', 'last', 'lname', 'l_name', 'surname', 'family name'],
        contains: ['last', 'surname', 'family', 'lname'],
        patterns: [/^l\.?\s*name$/i, /^last/i, /surname$/i]
      },
      jobName: {
        exact: ['job name', 'jobname', 'job', 'project name', 'project', 'job description', 'work description'],
        contains: ['job', 'project', 'work'],
        patterns: [/^job.*name$/i, /^project.*name$/i, /description$/i]
      },
      jobNumber: {
        exact: ['job number', 'jobnumber', 'job #', 'job no', 'project number', 'project #', 'project no', 'work order'],
        contains: ['job', 'project', 'work order'],
        patterns: [/^job.*#?$/i, /^project.*#?$/i, /number$/i, /^wo$/i]
      },
      activityCode: {
        exact: ['activity code', 'activitycode', 'code', 'dist activity code', 'distribution activity code', 'activity', 'cost code'],
        contains: ['activity', 'dist activity', 'cost code', 'code'],
        patterns: [/activity.*code/i, /dist.*activity/i, /cost.*code/i, /^code$/i]
      },
      activityDescription: {
        exact: ['activity description', 'activitydescription', 'description', 'activity desc', 'activity name', 'work description'],
        contains: ['activity', 'description', 'desc'],
        patterns: [/activity.*desc/i, /work.*desc/i, /description$/i]
      },
      payType: {
        exact: ['pay type', 'paytype', 'type', 'payment type', 'rate type', 'hour type', 'time type'],
        contains: ['pay', 'type', 'rate type'],
        patterns: [/pay.*type/i, /rate.*type/i, /time.*type/i, /^type$/i]
      },
      hours: {
        exact: ['hours', 'hour', 'hrs', 'hr', 'time', 'duration', 'total hours'],
        contains: ['hour', 'hrs', 'time'],
        patterns: [/^h\.?r\.?s?$/i, /hours?$/i, /time$/i]
      },
      burdened: {
        exact: ['burdened rate', 'burdenedrate', 'burdened', 'rate', 'hourly rate', 'burden rate', 'loaded rate'],
        contains: ['burdened', 'burden', 'rate', 'loaded'],
        patterns: [/burden.*rate/i, /loaded.*rate/i, /hourly.*rate/i, /^rate$/i]
      },
      total: {
        exact: ['total', 'amount', 'sum', 'total amount', 'total cost', 'cost', 'value'],
        contains: ['total', 'amount', 'cost'],
        patterns: [/total.*amount/i, /total.*cost/i, /^total$/i, /^amount$/i]
      }
    };
    
    const newMapping = { ...columnMapping };
    
    // Smart matching function with scoring
    const findBestMatch = (field: string, mappings: any) => {
      let bestMatch = '';
      let bestScore = 0;
      
      headersToUse.forEach(header => {
        const headerLower = header.toLowerCase().trim();
        let score = 0;
        
        // Exact match (highest priority)
        if (mappings.exact.includes(headerLower)) {
          score = 100;
        }
        // Contains match (medium priority)
        else if (mappings.contains.some((term: string) => headerLower.includes(term.toLowerCase()))) {
          score = 75;
        }
        // Pattern match (lower priority)
        else if (mappings.patterns.some((pattern: RegExp) => pattern.test(header))) {
          score = 50;
        }
        // Partial word match (lowest priority)
        else {
          const words = headerLower.split(/[\s_-]+/);
          const matchingWords = words.filter(word => 
            mappings.exact.includes(word) || 
            mappings.contains.some((term: string) => word.includes(term.toLowerCase()))
          );
          if (matchingWords.length > 0) {
            score = 25 * matchingWords.length;
          }
        }
        
        // Bonus for exact field name matches
        if (headerLower === field.toLowerCase()) {
          score += 25;
        }
        
        if (score > bestScore && score > 20) { // Minimum threshold
          bestScore = score;
          bestMatch = header;
        }
      });
      
      return bestMatch;
    };
    
    // Auto-map columns with smart matching
    Object.entries(smartColumnMappings).forEach(([field, mappings]) => {
      if (!newMapping[field as keyof typeof columnMapping]) {
        const matchedHeader = findBestMatch(field, mappings);
        if (matchedHeader) {
          (newMapping as any)[field] = matchedHeader;
        }
      }
    });
    
    setColumnMapping(newMapping);
    return newMapping;
  };

  // Process Excel file with mapping
  const processExcelFile = useCallback(async () => {
    if (!excelFile) return;
    
    setIsLoading(true);
    setError(null);
    setSkippedPayTypes([]);

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // Determine if we need to use header option based on the column mapping
        // If any of the mapped columns are letter-based (A, B, C...), we need to use header option
        const hasLetterHeaders = Object.values(columnMapping).some(value => 
          value && /^[A-Z]$/.test(value)
        );
        
        // Read the data with appropriate options
        let jsonData: any[];
        if (hasLetterHeaders) {
          // For files without headers, use A, B, C as headers
          jsonData = XLSX.utils.sheet_to_json(worksheet, { 
            raw: false,
            header: 'A'
          });
        } else {
          // For files with headers, use default behavior
          jsonData = XLSX.utils.sheet_to_json(worksheet, { raw: false });
        }

        // Process data into required format
        const processedData: ProcessedTimesheetData[] = [];
        const skippedPayTypeCounts: { [value: string]: number } = {};

        // First pass: collect unique jobs for invoice assignment. Jobs are
        // grouped by job number when one exists; when auto-split can't find
        // a job number in the job name (e.g. "Corporate Interiors OPS" has
        // no leading job code), we group by the job name text instead so
        // those rows still get their own invoice instead of all collapsing
        // into a single shared fallback invoice.
        const uniqueJobs = new Set<string>();
        for (const item of jsonData) {
          let jobNumber = item[columnMapping.jobNumber] || '';
          let jobDesc = item[columnMapping.jobName] || '';

          // Auto-split job name/number if enabled and jobNumber is empty but jobDesc has content
          if (autoSplitJobName && jobDesc && (!columnMapping.jobNumber || !jobNumber)) {
            console.log('Auto-splitting job name (first pass):', jobDesc);
            const { jobNumber: extractedNumber } = splitJobNameNumber(jobDesc);
            if (extractedNumber) {
              console.log('Successfully extracted job number (first pass):', extractedNumber);
              jobNumber = extractedNumber;
            } else {
              console.log('No job number pattern found (first pass) in:', jobDesc);
            }
          }

          const jobGroupKey = String(jobNumber).trim() || String(jobDesc).trim();
          if (jobGroupKey) {
            uniqueJobs.add(jobGroupKey);
          }
        }
        
        // Get the starting invoice numbers for all companies (like batch converter)
        const startingNumbers: { [prefix: string]: number } = {};
        const companies = ['CEC', 'REL', 'BTC'];
        
        for (const company of companies) {
          try {
            const nextNumber = await invoiceNumberingService.peekNextInvoiceNumber(company);
            startingNumbers[company] = parseInt(company === 'CEC' ? nextNumber : nextNumber.split('-')[1]);
          } catch (error) {
            console.error(`Error getting starting number for ${company}:`, error);
            startingNumbers[company] = 1001;
          }
        }

        // Track how many invoices we've assigned per company
        const invoiceCounters: { [prefix: string]: number } = {
          'CEC': 0,
          'REL': 0,
          'BTC': 0
        };

        // Pre-assign consecutive invoice numbers for all unique jobs
        const jobInvoiceMap: any = {};
        
        // Determine contractor prefix from selected contractor
        let companyPrefix = 'CEC'; // Default
        if (selectedContractor) {
          const contractorName = selectedContractor.name.toLowerCase();
          if (contractorName.includes('btc') || contractorName.includes('build.test.connect') || contractorName.includes('build test connect')) {
            companyPrefix = 'BTC';
          } else if (contractorName.includes('reliable') || contractorName.includes('rel')) {
            companyPrefix = 'REL';
          } else if (contractorName.includes('cec') || contractorName.includes('commonwealth')) {
            companyPrefix = 'CEC';
          }
        }
        
        for (const jobNumber of uniqueJobs) {
          let invoiceNumber;
          if (companyPrefix === 'CEC') {
            invoiceNumber = startingNumbers[companyPrefix] + invoiceCounters[companyPrefix];
            jobInvoiceMap[jobNumber] = invoiceNumber.toString();
          } else {
            invoiceNumber = startingNumbers[companyPrefix] + invoiceCounters[companyPrefix];
            jobInvoiceMap[jobNumber] = `${companyPrefix}-${invoiceNumber}`;
          }
          invoiceCounters[companyPrefix]++; // Increment for next job
        }
        
        // Second pass: process each row with pre-assigned invoice numbers
        for (const item of jsonData) {
          const firstName = item[columnMapping.firstName] || '';
          const lastName = item[columnMapping.lastName] || '';
          const employeeName = `${firstName} ${lastName}`.trim();
          
          let jobDesc = item[columnMapping.jobName] || '';
          let jobNumber = item[columnMapping.jobNumber] || '';
          
          // Auto-split job name/number if enabled and jobNumber is empty but jobDesc has content
          if (autoSplitJobName && jobDesc && (!columnMapping.jobNumber || !jobNumber)) {
            console.log('Auto-splitting job name:', jobDesc);
            const { jobNumber: extractedNumber, jobName: extractedName } = splitJobNameNumber(jobDesc);
            if (extractedNumber) {
              console.log('Successfully extracted job number:', extractedNumber, 'and job name:', extractedName);
              jobNumber = extractedNumber;
              jobDesc = extractedName;
            } else {
              console.log('No job number pattern found in:', jobDesc);
            }
          }
          
          const activityCode = item[columnMapping.activityCode] || '';
          const activityDesc = item[columnMapping.activityDescription] || '';
          const payType = item[columnMapping.payType] || '';
          const hours = parseFloat(item[columnMapping.hours] || 0);
          const burdenedRate = cleanCurrency(item[columnMapping.burdened] || 0);
          const total = cleanCurrency(item[columnMapping.total] || 0);

          // Use the manually entered week ending date
          const weekEnding = manualWeekEndingDate || '';

          // Store the week ending date for pay rate change checks
          if (weekEnding && !weekEndingDate) {
            setWeekEndingDate(weekEnding);
          }

          // Must match the grouping key used in the first pass above,
          // otherwise rows whose job name has no extractable job number
          // (e.g. "Corporate Interiors OPS") all fall back to the same
          // hardcoded invoice number and get merged with unrelated jobs.
          const jobGroupKey = String(jobNumber).trim() || String(jobDesc).trim();

          // Create base data
          const baseData = {
            'INV #': jobInvoiceMap[jobGroupKey] || '1001', // Fallback only if job has no number AND no name
            'EMPLOYEE': employeeName,
            'JOB NAME': jobDesc,
            'Activity Code': activityCode,
            'Activity Description': activityDesc,
            // If no job number could be extracted, fall back to the job
            // name so the field isn't blank on the invoice/PDF filename.
            'JOB NUMBER': jobNumber || jobDesc,
            'WEEK ENDING': weekEnding,
            // Raw Amount/Total column value from the source sheet (if
            // mapped), carried through so ExcelInvoiceGenerator can
            // reconcile it against what actually gets totaled into invoices.
            'SOURCE AMOUNT': total,
          };
          
          // Create entry based on pay type (case-insensitive, trimmed so
          // "regular", "REGULAR ", etc. from the source sheet still match)
          const normalizedPayType = String(payType).trim().toLowerCase();

          if (normalizedPayType === 'overtime') {
            const overtimeEntry: ProcessedTimesheetData = {
              ...baseData,
              'PAY TYPE': 'Overtime',
              'HOURS': parseFloat(hours.toFixed(2)),
              'BURDENED RATE': burdenedRate,
            };
            processedData.push(overtimeEntry);
          } else if (normalizedPayType === 'regular') {
            const regularEntry: ProcessedTimesheetData = {
              ...baseData,
              'PAY TYPE': 'Regular',
              'HOURS': parseFloat(hours.toFixed(2)),
              'BURDENED RATE': burdenedRate,
            };
            processedData.push(regularEntry);
          } else {
            // Unrecognized pay type (e.g. Holiday, Vacation, Bonus) - do NOT
            // silently drop these hours. Track them so we can warn the user
            // instead of having them vanish from the invoice total.
            const label = String(payType).trim() || '(blank)';
            skippedPayTypeCounts[label] = (skippedPayTypeCounts[label] || 0) + 1;
          }
        }

        setSkippedPayTypes(
          Object.entries(skippedPayTypeCounts).map(([value, count]) => ({ value, count }))
        );
        
        // Format columns and ensure no NaN values
        processedData.forEach(row => {
          // Ensure numeric values are valid
          const rate = typeof row['BURDENED RATE'] === 'number' && !isNaN(row['BURDENED RATE']) ? 
            row['BURDENED RATE'] : 0;
          
          // Store numeric values without currency formatting for Excel calculations
          row['BURDENED RATE'] = parseFloat(rate.toFixed(2));
          
          // Ensure hours are properly formatted
          row['HOURS'] = typeof row['HOURS'] === 'string' ? 
            parseFloat(row['HOURS']) : row['HOURS'];
        });
        
        setProcessedData(processedData);
        setShowMapping(false);
        setIsLoading(false);
      } catch (err) {
        console.error('Error processing file:', err);
        setError(`Error processing file: ${err}`);
        setIsLoading(false);
      }
    };
    
    reader.onerror = () => {
      setError('Error reading file');
      setIsLoading(false);
    };
    
    reader.readAsArrayBuffer(excelFile);
  }, [columnMapping, excelFile, manualWeekEndingDate, autoSplitJobName, weekEndingDate, selectedContractor]);

  const exportToExcel = () => {
    if (!processedData) return;
    
    // Create a deep copy of the data to avoid modifying the original
    const exportData = processedData.map(row => {
      // Create a new object with the same properties but without the $ symbol
      const newRow: any = {};
      
      // Copy all properties except BURDENED RATE and TOTAL
      Object.keys(row).forEach(key => {
        if (key !== 'BURDENED RATE' && key !== 'HOURS') {
          newRow[key] = (row as any)[key];
        }
      });
      
      // Convert string numbers to actual numbers for Excel without $ symbol
      newRow['BURDENED RATE'] = Number(row['BURDENED RATE']);
      newRow['HOURS'] = Number(row['HOURS']);
      
      return newRow;
    });
    
    // Create a new workbook
    const wb = XLSX.utils.book_new();
    
    // Set cell formats for numeric columns
    const ws = XLSX.utils.json_to_sheet(exportData);
    
    // Get the range of the sheet
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    
    // Find the column indices for BURDENED RATE and TOTAL
    const headers = Object.keys(exportData[0]);
    const burdenedRateIndex = headers.indexOf('BURDENED RATE');
    const hoursIndex = headers.indexOf('HOURS');
    
    // Set the cell format for numeric columns
    for (let R = range.s.r + 1; R <= range.e.r; ++R) {
      // BURDENED RATE column
      if (burdenedRateIndex !== -1) {
        const burdenedRateCell = XLSX.utils.encode_cell({r: R, c: burdenedRateIndex});
        if (!ws[burdenedRateCell]) continue;
        ws[burdenedRateCell].t = 'n'; // Set cell type to number
        ws[burdenedRateCell].z = '0.00'; // Format as number with 2 decimal places
      }
      
      // HOURS column
      if (hoursIndex !== -1) {
        const hoursCell = XLSX.utils.encode_cell({r: R, c: hoursIndex});
        if (!ws[hoursCell]) continue;
        ws[hoursCell].t = 'n'; // Set cell type to number
        ws[hoursCell].z = '0.00'; // Format as number with 2 decimal places
      }
    }
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Timesheet Summary');
    
    // Generate Excel file and trigger download
    XLSX.writeFile(wb, 'timesheet_summary.xlsx');
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      extractHeaders(acceptedFiles[0]);
    }
  }, [extractHeaders]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    }
  });

  // Render a select field for column mapping
  const renderSelectField = (label: string, field: keyof typeof columnMapping, isRequired = false) => {
    const isSelected = !!columnMapping[field];
    
    return (
      <div className={`space-y-2 bg-white rounded-lg p-4 border ${isSelected ? 'border-green-300 shadow-md' : 'border-gray-200'} transition-all duration-300`}>
        <div className="flex justify-between items-center">
          <label className="block text-sm font-medium text-gray-700 flex items-center">
            {label}
            {isRequired && <span className="text-red-500 ml-1">*</span>}
            {isSelected && (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <svg className="mr-1 h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Mapped
              </span>
            )}
          </label>
          {columnExamples[columnMapping[field]] && (
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-md">
              Example: "{columnExamples[columnMapping[field]]}"
            </span>
          )}
        </div>
        <div className="relative">
          <select 
            value={columnMapping[field]} 
            onChange={(e) => setColumnMapping({...columnMapping, [field]: e.target.value})}
            className={`w-full border ${isSelected ? 'border-green-300 bg-green-50' : 'border-gray-300'} rounded-md shadow-sm p-2 pr-10 appearance-none focus:ring-blue-500 focus:border-blue-500`}
          >
            <option value="">Select column</option>
            {excelHeaders.map(header => (
              <option key={header} value={header}>
                {header} {columnExamples[header] ? `(e.g., "${columnExamples[header]}")` : ''}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
            <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
              <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
            </svg>
          </div>
        </div>
        {field === 'firstName' && !isSelected && (
          <p className="text-xs text-gray-500 mt-1">Select the column containing employee first names</p>
        )}
        {field === 'lastName' && !isSelected && (
          <p className="text-xs text-gray-500 mt-1">Select the column containing employee last names</p>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto p-8">
      {/* File Upload Section */}
      <div className="mb-8 bg-white shadow-xl rounded-xl overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-8 py-6 text-white">
          <h1 className="text-3xl font-bold text-center mb-2">Timesheet Processor</h1>
          <p className="text-center text-blue-100">
            Upload a timesheet Excel file to process and format it for invoice generation
          </p>
        </div>
        
        <div className="p-8">
          <div 
            {...getRootProps()} 
            className="border-2 border-dashed border-blue-300 rounded-xl p-10 text-center cursor-pointer transition-all duration-300 hover:border-blue-500 hover:bg-blue-50"
          >
            <input {...getInputProps()} />
            <div className="flex flex-col items-center">
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className={`h-16 w-16 mb-4 ${isDragActive ? 'text-blue-600' : 'text-blue-400'}`} 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={1.5} 
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" 
                />
              </svg>
              {isDragActive ? (
                <p className="text-blue-600 text-lg font-medium">Drop the Excel file here...</p>
              ) : (
                <>
                  <p className="text-gray-700 text-lg font-medium mb-2">Drag and drop a timesheet Excel file here</p>
                  <p className="text-gray-500">or click to select a file</p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex justify-center items-center my-8 bg-white p-8 rounded-xl shadow-lg">
          <div className="flex flex-col items-center">
            <svg className="animate-spin h-12 w-12 text-blue-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-blue-600 text-lg font-medium">Processing file...</span>
            <p className="text-gray-500 mt-2 text-center max-w-md">
              This may take a moment depending on the size of your Excel file.
            </p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="my-8 bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-red-600 px-6 py-4">
            <div className="flex items-center">
              <svg className="h-6 w-6 text-white mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h3 className="text-lg font-medium text-white">Error Processing File</h3>
            </div>
          </div>
          <div className="px-6 py-4 bg-red-50">
            <p className="text-red-800">{error}</p>
          </div>
          <div className="px-6 py-3 bg-gray-50">
            <p className="text-sm text-gray-600">
              Please check your file format and try again. Make sure it's a valid Excel file with the expected data structure.
            </p>
          </div>
        </div>
      )}

      {/* Column Mapping UI */}
      {showMapping && !isLoading && (
        <div className="mb-8 bg-white shadow-xl rounded-xl overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-8 py-6 text-white">
            <h2 className="text-2xl font-bold text-center mb-2">Map Excel Columns</h2>
            <p className="text-center text-blue-100">
              Match your Excel columns to the required fields for invoice generation
            </p>
          </div>
          
          <div className="p-8">
            <div className="bg-blue-50 rounded-lg p-4 mb-6 border-l-4 border-blue-500">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    Smart auto-mapping has been applied! The system automatically detected common column patterns like "Dist Activity Code" and mapped them for you. 
                    Review the mappings below and adjust as needed. Fields marked with <span className="text-red-500">*</span> are required.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Progress indicator */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Mapping Progress</span>
                <span className="text-sm font-medium text-gray-700">
                  {Object.values(columnMapping).filter(Boolean).length} of 10 fields mapped
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-500" 
                  style={{ width: `${(Object.values(columnMapping).filter(Boolean).length / 10) * 100}%` }}
                ></div>
              </div>
            </div>
            
            {/* Quick actions */}
            <div className="mb-6 flex flex-wrap gap-2">
              <button 
                onClick={() => {
                  const newMapping = autoMapColumns();
                  
                  // Show feedback about what was mapped
                  const mappedCount = Object.values(newMapping).filter(Boolean).length;
                  if (mappedCount > 0) {
                    alert(`Smart mapping complete! Automatically mapped ${mappedCount} columns. Review the mappings below and adjust as needed.`);
                  } else {
                    alert('No automatic mappings found. Column headers might be using non-standard naming. Please map columns manually.');
                  }
                }} 
                className="bg-indigo-500 hover:bg-indigo-600 text-white font-medium py-2 px-4 rounded-lg shadow-sm transition-all duration-200 flex items-center justify-center text-sm"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                🚀 Smart Auto-Map
              </button>
              
              <button 
                onClick={() => {
                  setColumnMapping({
                    firstName: '',
                    lastName: '',
                    jobName: '',
                    jobNumber: '',
                    activityCode: '',
                    activityDescription: '',
                    payType: '',
                    hours: '',
                    burdened: '',
                    total: ''
                  });
                }} 
                className="bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-lg shadow-sm transition-all duration-200 flex items-center justify-center text-sm"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Reset Mapping
              </button>
            </div>
            
            {/* Column preview */}
            {Object.values(columnMapping).some(Boolean) && (
              <div className="mb-6 bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Column Preview</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  {Object.entries(columnMapping).filter(([_, value]) => value).map(([field, header]) => (
                    <div key={field} className="bg-white p-2 rounded border border-gray-200 text-xs">
                      <span className="font-medium text-gray-700">{field}:</span>
                      <span className="ml-1 text-gray-600">{header}</span>
                      {columnExamples[header] && (
                        <div className="mt-1 text-gray-500 truncate" title={columnExamples[header]}>
                          Example: {columnExamples[header]}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Week Ending Date Section - moved from processed data UI */}
            <div className="mb-6 bg-blue-50 rounded-lg p-4 border-l-4 border-blue-500">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="ml-3 w-full">
                  <h3 className="text-lg font-medium text-blue-800">Week Ending Date</h3>
                  <p className="text-sm text-blue-600 mb-2">
                    Enter the week ending date (Sunday) for this timesheet
                  </p>
                  <div className="flex items-center">
                    <input
                      type="date"
                      id="manualWeekEndingDate"
                      value={manualWeekEndingDate}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        
                        // Parse the date using the input value directly to avoid timezone issues
                        // Create date at noon to avoid timezone offset issues
                        const [year, month, day] = inputValue.split('-').map(num => parseInt(num));
                        const selectedDate = new Date(year, month - 1, day, 12, 0, 0); // months are 0-indexed
                        
                        if (selectedDate.getDay() !== 0) { // 0 is Sunday
                          // Find the next Sunday
                          const daysUntilSunday = 7 - selectedDate.getDay();
                          selectedDate.setDate(selectedDate.getDate() + daysUntilSunday);
                          
                          // Format back to YYYY-MM-DD
                          const adjustedYear = selectedDate.getFullYear();
                          const adjustedMonth = String(selectedDate.getMonth() + 1).padStart(2, '0');
                          const adjustedDay = String(selectedDate.getDate()).padStart(2, '0');
                          const adjustedDateString = `${adjustedYear}-${adjustedMonth}-${adjustedDay}`;
                          
                          setManualWeekEndingDate(adjustedDateString);
                          setWeekEndingDate(adjustedDateString);
                          alert(`Week ending date must be a Sunday. Adjusted to the next Sunday: ${adjustedMonth}/${adjustedDay}/${adjustedYear}`);
                        } else {
                          setManualWeekEndingDate(inputValue);
                          setWeekEndingDate(inputValue);
                        }
                      }}
                      className="w-full md:w-1/2 border border-gray-300 rounded-md shadow-sm p-2"
                    />
                    <div className="ml-2 text-sm text-gray-500">
                      {manualWeekEndingDate && (() => {
                        // Parse date safely to avoid timezone issues
                        const [year, month, day] = manualWeekEndingDate.split('-').map(num => parseInt(num));
                        const displayDate = new Date(year, month - 1, day, 12, 0, 0);
                        return displayDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contractor Selection */}
            <div className="mb-6 bg-green-50 rounded-lg p-4 border-l-4 border-green-500">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div className="ml-3 w-full">
                  <h3 className="text-lg font-medium text-green-800">Contractor Selection</h3>
                  <p className="text-sm text-green-600 mb-2">
                    Select the contractor for this timesheet to apply the correct burdened rate
                  </p>
                  <select
                    value={selectedContractor?.id || ''}
                    onChange={(e) => {
                      const contractor = contractors.find(c => c.id === e.target.value);
                      setSelectedContractor(contractor || null);
                    }}
                    className="w-full md:w-1/2 border border-gray-300 rounded-md shadow-sm p-2"
                    required
                  >
                    <option value="">Select Contractor</option>
                    {contractors.map((contractor) => (
                      <option key={contractor.id} value={contractor.id}>
                        {contractor.name} ({contractor.markupPercentage === 25 ? '25%' : `${contractor.markupPercentage.toFixed(2)}`} markup)
                      </option>
                    ))}
                  </select>
                  {selectedContractor && (
                    <div className="mt-2 text-sm text-green-700 bg-green-100 p-2 rounded">
                      <strong>Selected:</strong> {selectedContractor.name} - {selectedContractor.markupPercentage === 25 ? '25%' : `${selectedContractor.markupPercentage.toFixed(2)}`} markup rate
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Required fields section */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-800 mb-3">Required Fields</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {renderSelectField('First Name', 'firstName', true)}
                {renderSelectField('Last Name', 'lastName', true)}
                {renderSelectField('Job Name', 'jobName', true)}
                {renderSelectField('Job Number', 'jobNumber', true)}
                {renderSelectField('Pay Type', 'payType', true)}
                {renderSelectField('Hours', 'hours', true)}
                {renderSelectField('Burdened Rate', 'burdened', true)}
              </div>
              
              {/* Auto-split Job Name/Number Option */}
              <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="autoSplitJobName"
                      type="checkbox"
                      checked={autoSplitJobName}
                      onChange={(e) => setAutoSplitJobName(e.target.checked)}
                      className="w-4 h-4 text-amber-600 bg-gray-100 border-gray-300 rounded focus:ring-amber-500 focus:ring-2"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="autoSplitJobName" className="font-medium text-amber-800">
                      Auto-split Combined Job Name/Number Field
                    </label>
                    <p className="text-amber-700 mt-1">
                      Enable this when Job Name contains both job number and name (e.g., "23-25020 LISD Hebron HS 20 Year Refresh" or "CI23-52 TIAA - FRISCO CORPORATE CENTER FL 1-9"). 
                      The system will automatically extract the job number and separate the job name.
                    </p>
                    <p className="text-amber-600 text-xs mt-1 font-medium">
                      💡 Only use this when Job Number field is empty but Job Name contains the combined data
                    </p>
                  </div>
                </div>
              </div>
              
              <h3 className="text-lg font-medium text-gray-800 mb-3">Optional Fields</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {renderSelectField('Activity Code', 'activityCode')}
                {renderSelectField('Activity Description', 'activityDescription')}
                {renderSelectField('Total', 'total')}
              </div>
            </div>
          
            <div className="flex flex-wrap gap-4">
              <button 
                onClick={() => {
                  // Check if all required fields are mapped
                  let requiredFields = ['firstName', 'lastName', 'jobName', 'jobNumber', 'payType', 'hours', 'burdened'];
                  
                  // If auto-split is enabled, jobNumber is not required to be mapped
                  if (autoSplitJobName) {
                    requiredFields = ['firstName', 'lastName', 'jobName', 'payType', 'hours', 'burdened'];
                  }
                  
                  const missingFields = requiredFields.filter(field => !columnMapping[field as keyof typeof columnMapping]);
                  
                  if (missingFields.length > 0) {
                    alert(`Please map the following required fields: ${missingFields.join(', ')}`);
                    return;
                  }
                  
                  // Contractor validation
                  if (!selectedContractor) {
                    alert('Please select a contractor before processing the timesheet.');
                    return;
                  }
                  
                  // Additional validation for auto-split
                  if (autoSplitJobName && columnMapping.jobNumber) {
                    alert('When using auto-split, please leave the Job Number field unmapped. The system will extract it from Job Name automatically.');
                    return;
                  }
                  
                  console.log('About to process with autoSplitJobName:', autoSplitJobName);
                  saveMapping();
                  processExcelFile();
                }} 
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg shadow-md transition-all duration-200 flex items-center justify-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Process with this Mapping
              </button>
              <button 
                onClick={saveMapping} 
                className="bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-6 rounded-lg shadow-md transition-all duration-200 flex items-center justify-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                </svg>
                Save Mapping for Future Use
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Processed Data UI */}
      {processedData && !isLoading && !showMapping && (
        <div className="mb-8 bg-white shadow-xl rounded-xl overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-8 py-6 text-white">
            <h2 className="text-2xl font-bold text-center mb-2">Processed Timesheet Data</h2>
            <p className="text-center text-blue-100 mt-1">Ready for invoice generation</p>
          </div>
          
          <div className="p-8">
            <div className="mb-6 flex flex-wrap gap-4">
              <button 
                onClick={exportToExcel} 
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg shadow-md transition-all duration-200 flex items-center justify-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Export to Excel
              </button>
              <button 
                onClick={() => {
                  // Include contractor information with the processed data
                  const dataWithContractor = {
                    timesheetData: processedData,
                    contractorInfo: selectedContractor
                  };
                  localStorage.setItem('processedTimesheetData', JSON.stringify(dataWithContractor));
                  navigate('/invoices/excel-generator');
                }} 
                className="bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-6 rounded-lg shadow-md transition-all duration-200 flex items-center justify-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Generate Invoices
              </button>
              <button 
                onClick={() => setShowMapping(true)} 
                className="bg-yellow-500 hover:bg-yellow-600 text-white font-medium py-3 px-6 rounded-lg shadow-md transition-all duration-200 flex items-center justify-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Adjust Column Mapping
              </button>
            </div>
            
            <div className="mb-6 bg-green-50 rounded-lg p-4 border-l-4 border-green-500">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-green-800">Automatic Invoice Numbering</h3>
                  <p className="text-sm text-green-700 mt-1">
                    Invoice numbers are automatically assigned based on the next available number for each company.
                    CEC invoices start from 1001, while REL and BTC invoices include their company prefix (e.g., REL-1001).
                  </p>
                </div>
              </div>
            </div>

            {skippedPayTypes.length > 0 && (
              <div className="mb-6 bg-red-50 rounded-lg p-4 border-l-4 border-red-500">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-red-800">
                      {skippedPayTypes.reduce((sum, s) => sum + s.count, 0)} row(s) were NOT included below - unrecognized pay type
                    </h3>
                    <p className="text-sm text-red-700 mt-1">
                      Only rows whose pay type is exactly "Regular" or "Overtime" get converted to invoice lines. These values didn't match and their hours are missing from every total below:
                    </p>
                    <ul className="text-sm text-red-700 mt-2 list-disc list-inside">
                      {skippedPayTypes.map(({ value, count }) => (
                        <li key={value}><span className="font-mono font-semibold">"{value}"</span> — {count} row(s)</li>
                      ))}
                    </ul>
                    <p className="text-sm text-red-700 mt-2">
                      If these should be invoiced, remap the "Pay Type" column or handle them separately before generating invoices.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="overflow-x-auto bg-white rounded-lg border border-gray-200 shadow-md">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">INV #</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">EMPLOYEE</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">JOB NAME</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">JOB NUMBER</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activity Code</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activity Description</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PAY TYPE</th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">HOURS</th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">BURDENED RATE</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">WEEK ENDING</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {processedData.map((row, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{row['INV #']}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row['EMPLOYEE']}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row['JOB NAME']}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row['JOB NUMBER']}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row['Activity Code']}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row['Activity Description']}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          row['PAY TYPE'] === 'Overtime' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {row['PAY TYPE']}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{row['HOURS']}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{row['BURDENED RATE']}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row['WEEK ENDING']}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimesheetProcessor;
